import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {SubCategory, Category} from "@/integrations/supabase/category.models.ts";
import {FundManager} from "@/integrations/supabase/metadata.models.ts";
import {log} from "node:util";
import logger from "@logger";

/**
 * MetadataService handles metadata-related operations such as categories, subcategories, types, etc. This is data
 * that cuts across all use-cases. This service provides a home for such data
 */
export class MetadataService extends BaseService {

    /**
     * Creates a new subcategory for a given category ID
     * @param {SubCategoryCreateRequest} request request
     */
    async createSubCategory(request: SubCategoryCreateRequest): Promise<SubCategory> {
        const payload = this.convertRequestToSnakeCase(request);
        try {
            const { data, error } = await supabase
                .from('sub_categories')
                .insert(payload)
                .select()
                .single();
            if (error) {
                throw error;
            }
            return data;
        } catch (e) {
            throw await this.handleError(e);
        }
    }

    /**
     * Fetches the category for a given category name and type
     * @param {string} name Name of the category to retrieve
     * @param {string} type Type of the category to retrieve
     */
    async fetchCategory(name: string, type: string): Promise<Category> {
        try {
            const { data, error } = await supabase
                .from('categories')
                .select()
                .eq('name', name)
                .eq('type', type)
                .single()

            // TODO: resolve to specific errors for the UI to consume and display
            if (error) {
                throw error;
            }
            return data;
        } catch (e) {
            await this.handleError(e);
        }
    }

    /**
     * Create a new category
     * @param {CategoryCreateRequest} request request to create a new category
     */
    async createCategory(request: CategoryCreateRequest): Promise<Category> {
        const payload = this.convertRequestToSnakeCase(request)
        try {
            const { data, error } = await supabase
                .from('categories')
                .insert(payload)
                .select()
                .single();
            if (error) {
                throw error;
            }
            return data
        } catch (e) {
            await this.handleError(e);
            throw e
        }
    }

    async seedDefaultCategories(userId: string): Promise<void> {
        try {
            const defaults = [
                { name: 'Salary', type: 'income', icon: '💰' },
                { name: 'Freelance', type: 'income', icon: '💻' },
                { name: 'Investments', type: 'income', icon: '📈' },
                { name: 'Food & Dining', type: 'expense', icon: '🍔' },
                { name: 'Transport', type: 'expense', icon: '🚗' },
                { name: 'Housing', type: 'expense', icon: '🏠' },
                { name: 'Utilities', type: 'expense', icon: '💡' },
                { name: 'Entertainment', type: 'expense', icon: '🎬' },
                { name: 'Shopping', type: 'expense', icon: '🛒' },
                { name: 'Health', type: 'expense', icon: '🏥' },
                { name: 'Education', type: 'expense', icon: '📚' },
                { name: 'Insurance', type: 'expense', icon: '🛡️' },
                { name: 'Other', type: 'expense', icon: '📦' },
            ];
            const { error } = await supabase
                .from('categories')
                .insert(defaults.map(c => ({ ...c, user_id: userId, is_default: true })));
            if (error) throw error;
        } catch (e) {
            await this.handleError(e);
            throw e
        }
    }

    /**
     * Fetch categories
     */
    async fetchCategories(): Promise<Category[]> {
        try {
            const { data, error } = await supabase
                .from('categories')
                .select('*')
                .order('name');
            // TODO: resolve to specific errors for the UI to consume and display
            if (error) {
                throw error;
            }
            return data
        } catch (e) {
            await this.handleError(e);
            throw e
        }
    }

    /**
     * Retrieves sub-category for a given sub category name and category ID
     * @param {string} name Name of the sub category to retrieve
     * @param {string} categoryId ID of the category to retrieve
     */
    async fetchSubCategory(name: string, categoryId: string): Promise<SubCategory> {
        try {
            const { data, error } = await supabase
                .from('sub_categories')
                .select()
                .eq('name', name)
                .eq('category_id', categoryId)
                .single()

            if (error) throw error;
            return data;
        } catch (e) {
            throw await this.handleError(e);
        }
    }

    /**
     * Fetches sub categories for the given category
     * @param {string} categoryId Category ID
     * @return {SubCategory[]} A list of sub categories for the provided category
     */
    async fetchSubCategoriesForCategory(categoryId: string): Promise<SubCategory[]> {
        try {
            const { data, error } = await supabase
                .from('sub_categories')
                .select('*')
                .eq('category_id', categoryId!)
                .order('name');
            if (error) throw error;
            return data
        } catch (e) {
            throw await this.handleError(e);
        }
    }

    /**
     * Fetches or creates a sub-category for a given sub category name and category ID
     * @param {string} userId User ID
     * @param {string} name Name of the sub category to retrieve
     * @param {string} categoryId ID of the category to retrieve
     */
    async fetchOrCreateSubCategory(userId: string, name: string, categoryId: string): Promise<SubCategory> {
        logger.info(`Fetching or creating sub category ${name} for category ${categoryId}`);
        const { data, error } = await supabase
            .from('sub_categories')
            .select()
            .eq('name', name)
            .eq('category_id', categoryId)
            .single()

        if (error) {
            await this.handleError(error);
            throw error;
        }

        if (!data) {
            return await this.createSubCategory({userId, name, categoryId});
        }
        return data;

    }

    /**
     * Creates fund manager
     * @param request
     */
    async createFundManager(request: FundManagerCreateRequest): Promise<FundManager> {
        logger.info(`Creating fund manager ${request.name}`);

        const { data, error } = await supabase
            .from('fund_managers')
            .insert({
                name: request.name,
                country: request.country,
                user_id: request.userId,
                is_default: false
            })
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Creates a new fund manager or fetches an existing one
     * @param request
     */
    async createOrFetchFundManager(request: FundManagerCreateRequest): Promise<FundManager> {
        logger.info(`Creating or retrieving fund manager ${request.name}`);
        try {
            const existingFundManager = await this.fetchFundManagerByName(request.userId, request.name);
            logger.info(`Retrieved existing fetching fund manager`, existingFundManager);
            return existingFundManager;
        } catch (error) {
            await this.handleError(error);
            logger.warn(`Error retrieving fund manager ${request.name}, creating a new one...: ${error.message}`);
            return await this.createFundManager(request);
        }
    }

    /**
     * Fetches fund manager by name
     * @param {string} name fund manager name
     * @param {string} userId User ID
     */
    async fetchFundManagerByName(userId: string, name: string): Promise<FundManager> {
        logger.info(`Fetching fund manager ${name}`);
        const { data, error } = await supabase
            .from('fund_managers')
            .select()
            .eq('name', name)
            .eq('user_id', userId)
            .single();

        if (error) {
            logger.error(`Error fetching fund manager ${name}: ${error.message}`);
            await this.handleError(error);
            throw error;
        }

        return data;
    }

}

export default new MetadataService();
